import { useMemo } from 'react';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormError } from '@/components/ui/form-error';
import { formatCpfCnpj } from '@/lib/formatters';
import { useCreditAnalysisCreateForm } from './use-create-credit-analysis';

interface CreateCreditAnalysisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (creditAnalysis: CreditAnalysis) => void;
}

export function CreateCreditAnalysisDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateCreditAnalysisDialogProps) {
  const {
    formError,
    clearFormError,
    form,
    handleSubmit,
    isSubmitting,
    accountSearchResults,
    isSearching,
    handleSearchChange,
    resetForm,
  } = useCreditAnalysisCreateForm({ onSuccess });

  const accountId = form.watch('accountId');
  const account = useMemo(
    () => accountSearchResults.find((a) => a.id === accountId) || null,
    [accountId, accountSearchResults],
  );

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Gerar nova análise de crédito
          </DialogTitle>
          <DialogDescription>
            Selecione o cliente para gerar uma nova análise de crédito.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            noValidate
            onSubmitCapture={clearFormError}
            onSubmit={handleSubmit}
            className="space-y-4 overflow-hidden"
          >
            <div>
              <div className="text-sm font-medium mb-1.5 block">Cliente</div>
              <Combobox
                disableLocalFilter
                type="single"
                data={accountSearchResults}
                placeholder="Digite o CPF, CNPJ, Razão Social ou Nome Fantasia..."
                value={account ? [account] : []}
                isLoading={isSearching}
                onOptionChange={(selected) => form.setValue('accountId', selected[0]?.id || '')}
                keyExtractor={(item) => item?.document?.value ?? item?.id}
                displayValueGetter={(item) =>
                  `${item?.social_name || item?.name} - ${formatCpfCnpj(item?.document?.value || '')}`
                }
                onSearchChange={handleSearchChange}
              />
              {form.formState.errors.accountId && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.accountId.message}
                </p>
              )}
            </div>

            <FormError error={formError} onDismiss={clearFormError} />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { useCreditAnalysisCreateForm as useCreateAnalysis };
